import moment from 'moment'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'

import NotificationBar from './NotificationBar'
import StreamOffline from './StreamOffline'
import './vjs-moal.css'

const STATUS_PRE = 'STATUS_PRE'
const STATUS_LIVE = 'STATUS_LIVE'
const STATUS_POST = 'STATUS_POST'

const StreamStarting = ({ videoURL, startTime, videoDuration }) => {
  const videoRef = useRef(null)
  const playerRef = useRef(null)

  const [status, setStatus] = useState(STATUS_PRE)
  const [muted, setMuted] = useState(false)
  const [browserBlocked, setBrowserBlocked] = useState(false)

  useEffect(() => {
    // Make sure Video.js player is only initialized once
    if (playerRef.current) return

    // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
    const videoElement = document.createElement('video-js')
    videoRef.current.appendChild(videoElement)

    const player = videojs(videoElement, {
      controls: true,
      autoplay: true,
      preload: 'auto',
      fill: true,
      playsinline: true,
      sources: [
        {
          src: videoURL
        }
      ]
    })

    playerRef.current = player
    player.addClass('vjs-moal')

    player.ready(() => {
      playVideo(player)
    })

    const checkInterval = setInterval(() => {
      playVideo(player)
    }, 1000)

    return () => {
      if (playerRef.current) {
        playerRef.current.dispose()
      }
      clearInterval(checkInterval)
    }
  }, [videoRef])

  const playVideo = (player) => {
    const now = moment()
    const start = moment(startTime)
    const end = start.clone().add(videoDuration, 'seconds')

    if (now.isBefore(start)) {
      player.pause()
      setStatus(STATUS_PRE)
      return
    } else if (now.isAfter(end)) {
      player.currentTime(videoDuration)
      player.pause()
      setStatus(STATUS_POST)
      return
    } else {
      setStatus(STATUS_LIVE)
      const offsetSeconds = Math.max(0, now.diff(start, 'seconds'))

      player.currentTime(offsetSeconds)

      const playPromise = player.play()
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Retry with muted autoplay if initial attempt fails
          player.muted(true)
          setMuted(true)
          player.play().catch(() => setBrowserBlocked(true))
        })
      }
    }
  }

  return (
    <div className="w-full h-full">
      <div className="relative w-full h-full bg-black">
        {muted && (
          <NotificationBar
            message="Warning: Audio is Muted"
            type="warning"
            cta={'Unmute'}
            onClick={() => {
              playerRef.current.muted(false)
              setMuted(false)
            }}
          />
        )}
        {browserBlocked && (
          <NotificationBar
            message="Autoplay of audio/video is blocked by your browser."
            type="warning"
            cta={'Help'}
            onClick={() => {
              window.open(
                '/audio-video-troubleshooting',
                '_blank',
                'noreferrer'
              )
            }}
          />
        )}
        {status === STATUS_PRE && <StreamOffline withWrapper />}
        {status === STATUS_POST && (
          <StreamOffline
            text="Stream will be starting shortly..."
            link=""
            imageKey="starting"
            withWrapper
          />
        )}
        <div
          className={`${status === STATUS_LIVE ? 'aspect-video resize-helper' : 'hidden'}`}
          ref={videoRef}
        />
      </div>
    </div>
  )
}

StreamStarting.propTypes = {
  videoURL: PropTypes.string.isRequired,
  startTime: PropTypes.string.isRequired, // ISO string expected
  videoDuration: PropTypes.number.isRequired // in seconds
}

export default StreamStarting
